<template>
    <BaseDialog v-model:open="isDocumentUploadDialogOpen" :title="dialogTitle" dialog-content-class="md:w-160" @interact-outside="handleCancel">
        <div class="p-1 max-w-(--breakpoint-md) w-full">
            <div class="bg-white rounded-lgr flex flex-col">
                <div class="flex justify-between items-center px-6 pt-5">
                    <h3 class="title-body">{{ dialogTitle }}</h3>
                    <button
                        type="button"
                        class="p-2 bi bi-x-lg text-xl/none text-valence-grey-400 hover:text-valence-grey-750 hover:bg-valence-grey-50 rounded-xl"
                        aria-label="Close Document Upload Dialog"
                        @click="handleCancel"></button>
                </div>
                <HorizontalDivider class="mt-4 mb-0!" />
                <div class="px-6 flex flex-col gap-2 my-4 body-small text-valence-grey-750">
                    <p>If you have any documents that will help Nadia support you with this step, you can upload them here. <span v-if="uploadsExamples">Examples:</span></p>
                    <ul v-if="uploadsExamples" class="list-disc ml-5 mb-3">
                        <li v-for="example in uploadsExamples" :key="example">{{ example }}</li>
                    </ul>
                    <AdditionalDocumentUploadSection
                        :is-disabled="isLinkingDocuments"
                        :upload-type="DOCUMENT_UPLOAD_TYPES.OTHER"
                        @document-uploaded="handleDocumentUploaded"
                        @document-removed="handleDocumentRemoved"
                        @document-upload-error="handleDocumentUploadError"
                        @document-selected="handleDocumentSelected" />
                </div>
                <HorizontalDivider class="my-0!" />
                <div class="px-6 py-4">
                    <div class="flex justify-end gap-3">
                        <BaseButton theme="neutral" :disabled="isLinkingDocuments" @click="handleCancel">Cancel</BaseButton>
                        <BaseButton theme="primary" :disabled="isSubmitting" class="flex gap-2 items-center" @click="handleStartDrafting">
                            <LoadingSpinner v-if="isSubmitting" class="h-4" />
                            Start chat
                        </BaseButton>
                    </div>
                </div>
            </div>
        </div>
        <BaseToast v-model:open="isErrorToastOpen" :description="errorMessage" />
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import AdditionalDocumentUploadSection from "~vue/Moments/AdditionalDocumentUploadSection.vue";
import { usePerformanceReviewDocumentEvents } from "~vue/Moments/composables/usePerformanceReviewDocumentEvents";
import { DOCUMENT_UPLOAD_TYPES } from "~vue/Moments/constants.js";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { computed, ref } from "vue";

interface UploadedDocument {
    id: number;
    upload_type: string;
}

const { review } = defineProps<{
    review: DecoratedPerformanceReview;
}>();
const emit = defineEmits(["save", "cancel"]);

const dialogTitle = "Anything to share before we begin?";
const uploadsExamples = computed(() => {
    if (review.cta?.uploads_examples?.length) {
        return review.cta.uploads_examples;
    }
    return ["Goals", "Feedback notes", "A previous performance review"];
});

const reviewType = computed(() => review.cta?.reviewer_target ?? "self");

const isDocumentUploadDialogOpen = defineModel("dialog", {
    type: Boolean,
    required: true,
});

const {
    handleDocumentSelected,
    handleDocumentUploadError,
    handleDocumentRemoved: logDocumentRemoved,
    handleSubmitted,
} = usePerformanceReviewDocumentEvents("review_document", review.id, reviewType);

// The "uploaded" analytics event is emitted server-side by the link_documents endpoint, so this
// handler only maintains local state.
const handleDocumentUploaded = (documents: UploadedDocument | Array<UploadedDocument>) => {
    if (Array.isArray(documents)) {
        uploadedDocuments.value.push(...documents);
    } else {
        uploadedDocuments.value.push(documents);
    }
};

const handleDocumentRemoved = (document: UploadedDocument) => {
    uploadedDocuments.value = uploadedDocuments.value.filter((doc) => doc.id !== document.id);
    logDocumentRemoved(document);
};

const isSubmitting = ref(false);
const isErrorToastOpen = ref(false);
const errorMessage = ref("");
const uploadedDocuments = ref<Array<UploadedDocument>>([]);

const linkUrl = computed(() => `/api/coach/performance_reviews/${review.id}/documents/`);
const documentPayload = computed(() => ({
    documents: uploadedDocuments.value.map((doc) => ({ file_id: doc.id, upload_type: doc.upload_type })),
    dialog: "review_document",
}));
const { execute: linkDocuments, isFetching: isLinkingDocuments } = useFetch(linkUrl, { immediate: false }).post(documentPayload).json();

const handleStartDrafting = async () => {
    if (isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    if (uploadedDocuments.value.length > 0) {
        try {
            await linkDocuments(true);
            handleSubmitted(uploadedDocuments.value);
        } catch {
            errorMessage.value = "There was an error linking your documents. Please try again.";
            isErrorToastOpen.value = true;
            isSubmitting.value = false;
            return;
        }
    }
    isSubmitting.value = false;
    emit("save", uploadedDocuments.value.length);
    uploadedDocuments.value = [];
    isDocumentUploadDialogOpen.value = false;
};

const handleCancel = () => {
    isDocumentUploadDialogOpen.value = false;
    emit("cancel");
};
</script>
